import type {
  ReciprocalContext,
  ReciprocalWarmth,
  TeamExpOutcome,
} from './types';

/**
 * Turning a feed or a warm into the two or three short lines a player should
 * see, and nothing more.
 *
 * One implementation, shared by the profile, the Grove and the Community page,
 * because those three are the same moment on three screens: a player clicked
 * Feed and wants to know what happened. Three copies of this phrasing would
 * drift the first time a rule changed, and the failure would be a player told
 * different things about the same action depending on where they clicked it —
 * the same argument `TrainerCard` makes for being shared with the editor.
 *
 * **Compact on purpose.** The design is explicit: no large modal every time.
 * Feeding is meant to be done dozens of times in a session, so this returns
 * toast-sized strings and the caller pushes at most three. A level-up gets its
 * own line because it is genuinely news; the rest is one line.
 */

export interface FeedToastLines {
  /** The headline: what happened to their Pokemon. */
  primary: string;
  /** What came back to your Team. Null when nothing did. */
  team: string | null;
  /** Level-ups, already collapsed into one line. Null when nobody levelled. */
  levels: string | null;
  /** A short reason the reward was unusual. Null most of the time. */
  note: string | null;
}

/**
 * "Your Team +2 EXP each" when every member got the same, which is the common
 * case under the ALL model at equal levels; otherwise the total, because six
 * different numbers in a toast is a table nobody reads.
 */
function teamLine(team: TeamExpOutcome): string | null {
  if (team.total <= 0 || team.awards.length === 0) {
    // A Team where everybody is level 100 is a real state and worth saying
    // once, rather than silently paying nothing.
    if (team.teamSize > 0 && team.eligible === 0) return 'Your Team is fully levelled.';
    return null;
  }

  const amounts = new Set(team.awards.map((award) => award.expGranted));
  if (amounts.size === 1 && team.awards.length > 1) {
    return `Your Team +${[...amounts][0]} EXP each`;
  }
  if (team.awards.length === 1) {
    const only = team.awards[0]!;
    return `${only.name} +${only.expGranted} EXP`;
  }
  return `Your Team +${team.total} EXP`;
}

/**
 * One line for however many levelled, never one toast each.
 *
 * Six separate celebrations for one click is the noise the design calls out by
 * name, and it is also just wrong about what happened: they levelled *because
 * of one action*, so it reads as one event.
 */
function levelLine(team: TeamExpOutcome): string | null {
  const levelled = team.awards.filter((award) => award.leveledUp);
  if (levelled.length === 0) return null;
  if (levelled.length === 1) {
    const only = levelled[0]!;
    return `${only.name} reached Lv ${only.toLevel}!`;
  }
  return `Your Team levelled up! ${levelled
    .map((award) => `${award.name} → Lv ${award.toLevel}`)
    .join(', ')}`;
}

/**
 * Why the reward was unusual — and only when it was.
 *
 * A note on every single feed is a note nobody reads, so the common case
 * returns null. Diminishing returns is the one worth surfacing every time it
 * applies: a player whose rewards have quietly halved should be told why, or
 * the system looks broken rather than deliberate.
 */
function reciprocalNote(reciprocal: ReciprocalContext | null | undefined): string | null {
  if (!reciprocal) return null;
  if (reciprocal.diminished) {
    return "You've helped this trainer a lot today — smaller reward. Try somebody new!";
  }
  if (reciprocal.npcTarget) return 'Living Trainers give a smaller reward than real ones.';
  if (reciprocal.firstVisitToday) return 'First time helping them today — bonus!';
  return null;
}

export function feedToast(result: {
  pokemonName: string;
  expGranted: number;
  coinsEarned?: number;
  friendBonus?: boolean;
  guildBonus?: boolean;
  diminished?: boolean;
  team?: TeamExpOutcome;
  reciprocal?: ReciprocalContext | null;
}): FeedToastLines {
  const bonuses = [
    result.friendBonus ? 'friend' : null,
    result.guildBonus ? 'guild' : null,
  ].filter(Boolean);

  const coins = result.coinsEarned ? `, +${result.coinsEarned} Coins` : '';
  const primary =
    `Fed ${result.pokemonName} +${result.expGranted} EXP` +
    (bonuses.length > 0 ? ` (${bonuses.join(' + ')} bonus)` : '') +
    coins;

  return {
    primary,
    team: result.team ? teamLine(result.team) : null,
    levels: result.team ? levelLine(result.team) : null,
    note: result.diminished
      ? 'This Pokémon has been fed a lot today, so its EXP was reduced.'
      : reciprocalNote(result.reciprocal),
  };
}

export interface WarmToastLines {
  primary: string;
  own: string | null;
  ready: string | null;
  note: string | null;
}

export function warmToast(result: {
  progressGranted: number;
  isReady: boolean;
  coinsEarned?: number;
  ownEgg?: ReciprocalWarmth | null;
  reciprocal?: ReciprocalContext | null;
}): WarmToastLines {
  const coins = result.coinsEarned ? `, +${result.coinsEarned} Coins` : '';

  return {
    primary: `Warmed their egg +${result.progressGranted} 🔥${coins}`,
    own: result.ownEgg ? `Your Priority Egg +${result.ownEgg.progressGranted} 🔥` : null,
    // The one thing here that genuinely deserves interrupting for. Everything
    // else in this file is a running total; this is a Pokemon waiting.
    ready: result.ownEgg?.isReady ? 'Your egg is ready to hatch!' : null,
    note: result.ownEgg
      ? null
      : // Said once, gently, rather than silently paying nothing back: an empty
        // hatchery is why the reward is missing, and it is fixable in one click.
        'Nothing incubating — put an egg in a slot to warm one of your own too.',
  };
}
